/**
 * Mirrors backend Analytics::PrepareId (`envPrefix.slice(0, 20) + '_' + dbId`).
 */
const ALREADY_PREPARED_REGEX = /^[^_=]{1,20}_\d+$/;

const envPrefix = (analyticsEnvironment: string): string =>
  analyticsEnvironment.trim().slice(0, 20);

const normalizeTreatmentPlanIdForAnalytics = (
  raw: string | undefined | null,
  analyticsEnvironment: string | undefined | null
): string | undefined | null => {
  if (raw === undefined || raw === null) return raw;

  const trimmed = raw.trim();

  if (trimmed === "") return raw;

  const env = analyticsEnvironment?.trim();

  if (!env) return raw;

  if (ALREADY_PREPARED_REGEX.test(trimmed)) return trimmed;

  try {
    const dbId = atob(trimmed).split("-").pop() ?? "";

    if (!/^\d+$/.test(dbId)) return raw;

    return `${envPrefix(env)}_${dbId}`;
  } catch {
    return raw;
  }
};

const normalizePlanIdValue = (planId: string, env: string): string => {
  const suffixIndex = planId.lastIndexOf("_");

  if (suffixIndex === -1) return planId;

  const treatmentPlanId = planId.slice(0, suffixIndex);
  const suffix = planId.slice(suffixIndex + 1);
  const normalizedId = normalizeTreatmentPlanIdForAnalytics(treatmentPlanId, env);

  return `${normalizedId}_${suffix}`;
};

export const normalizePlanIdsInFsAnalyticsPayload = <T extends Record<string, unknown>>(
  payload: T,
  analyticsEnvironment: string | undefined | null
): T => {
  const env = analyticsEnvironment?.trim();

  if (!env) return payload;

  let result = payload;

  if (
    Object.prototype.hasOwnProperty.call(payload, "treatment_plan_id") &&
    typeof payload.treatment_plan_id === "string"
  ) {
    const normalized = normalizeTreatmentPlanIdForAnalytics(payload.treatment_plan_id, env);

    if (normalized !== payload.treatment_plan_id) {
      result = { ...result, treatment_plan_id: normalized };
    }
  }

  if (Object.prototype.hasOwnProperty.call(payload, "plan_id") && Array.isArray(payload.plan_id)) {
    const planIds = payload.plan_id;
    const normalizedPlanIds = planIds.map(id =>
      typeof id === "string" ? normalizePlanIdValue(id, env) : id
    );

    if (normalizedPlanIds.some((id, index) => id !== planIds[index])) {
      result = result === payload ? { ...payload } : result;
      result = { ...result, plan_id: normalizedPlanIds };
    }
  }

  return result;
};

export { normalizeTreatmentPlanIdForAnalytics };
